import { getFirestore, getCloudStorage, getFirebaseAuth, getArrayRemove /*, getArrayUnion*/, getFirestoreIncrementField } from '../../system/firebase';

import {
    SNACK_BAR_MESSAGE_OPERATION_FAILED,
    SNACK_BAR_MESSAGE_SAVE,
    SNACK_BAR_MESSAGE_INVITATION,
    ACCOUNT_STATUS_ACTIVE,
    ERROR_MESSAGE_INVITEE_EXISTS_IN_MEMBERS,
    ERROR_MESSAGE_INVITEE_PENDING,
    SNACK_BAR_MESSAGE_DELETE_TEAM_MEMBER,
    UPLOADED_VIDEO_STATUS_PROCESSING,
    UPLOADED_VIDEO_STATUS_APPROVED,
} from '../constants';
import { mapDefinedFields, deleteEmptyFields } from '../dtoHelper';
import { convertFirebaseTimestampsToDate } from '../dateHelper';
import { activeEmpMemberAfterSetUp } from './authServices';
import {
    requestCancelAdditionalSeatSubscriptionAtPeriodEnd,
    requestCancelNewAdditionalSeatSubscriptionNow,
    requestCancelNewSubscriptionNow,
    requestCancelSubscriptionAtPeriodEnd,
    requestCancelSubscriptionAtTierPeriodEnd,
    scheduleStripeSubscriptionDowngrade,
    setDefaultCardForStripeCustomer,
} from './paymentServices';

import { format as formatDate, addMonths, addYears } from 'date-fns';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import _findIndex from 'lodash/findIndex';
import _toLower from 'lodash/toLower';
import _isNil from 'lodash/isNil';
import _defaultTo from 'lodash/defaultTo';

export const saveEmployerOwnerData = async ({
    companyName,
    dateFormat,
    language,
    country,
    state,
    city,
    cityLocation,
    newCity,
    memberCountry,
    memberState,
    memberCity,
    memberCityLocation,
    newMemberCity,
    employerId,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    snackBarMessage,
}) => {
    const db = getFirestore();
    const currentDt = new Date();

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    try {
        await db.runTransaction(async transaction => {
            // This code may get re-run multiple times if there are conflicts.
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);

            if (employerDoc.exists) {
                const employerData = employerDoc.data();
                const firstMember = _get(employerData, 'members[0]', {});

                // include all fields plus the new fields
                const employerDto = {
                    ...employerData,
                    companyName,
                    dateFormat,
                    language,
                    country,
                    state,
                    city,
                    cityLocation,
                };

                const ownerMemberDto = {
                    country: memberCountry,
                    state: memberState,
                    city: memberCity,
                    cityLocation: memberCityLocation,
                };

                // include new fields in the first member
                _set(employerDto, 'members[0]', { ...firstMember, ...ownerMemberDto });

                //-------------------------------------------------------------------------------------------
                // write employers
                //-------------------------------------------------------------------------------------------
                await transaction.set(employerRef, mapDefinedFields(employerDto), { merge: true });
            } else {
                throw new Error('Employer account does not exist.');
            }
        });

        //-------------------------------------------------------------------------------------------
        // add new LOV options, if any
        //-------------------------------------------------------------------------------------------
        const batch = db.batch();
        const newLovOptionRef = db.collection('newLovOptions').doc();
        const newLovOptionsDto = {};

        // new company city
        if (newCity) {
            _set(newLovOptionsDto, 'newCityOption', newCity);
            _set(newLovOptionsDto, 'newCityOptionState', _defaultTo(state, 'Unknown State'));
            _set(newLovOptionsDto, 'newCityOptionCountry', _defaultTo(country, 'Unknown Country'));
        }
        if (!_isEmpty(newLovOptionsDto)) {
            batch.set(newLovOptionRef, {
                ...newLovOptionsDto,
                createDt: currentDt,
                createBy: employerId,
            });
        }

        // new owner member city
        if (newMemberCity) {
            _set(newLovOptionsDto, 'newCityOption', newMemberCity);
            _set(newLovOptionsDto, 'newCityOptionState', _defaultTo(memberState, 'Unknown State'));
            _set(newLovOptionsDto, 'newCityOptionCountry', _defaultTo(memberCountry, 'Unknown Country'));
        }
        if (!_isEmpty(newLovOptionsDto)) {
            batch.set(newLovOptionRef, {
                ...newLovOptionsDto,
                createDt: currentDt,
                createBy: employerId,
            });
        }

        await batch.commit();

        setSnackbarMessage(snackBarMessage ? snackBarMessage : SNACK_BAR_MESSAGE_SAVE);

        return true;
    } catch (error) {
        console.error('Transaction failed.', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const updateEmployerData = async ({ data, employerId, callback, setSnackbarMessage, setSnackbarErrorMessage, snackBarMessage }) => {
    const db = getFirestore();
    const batch = db.batch();
    const currentDt = new Date();
    const urlName = data?.urlName;
    const urlNameLower = urlName ? _toLower(urlName) : null;
    const employerRef = db.collection('employers').doc(employerId);
    try {
        let _data = mapDefinedFields({ ...data });
        batch.update(employerRef, mapDefinedFields({ ..._data, urlNameLower, profilePicUrl: data.profilePicUrl }));

        const newLovOptionRef = db.collection('newLovOptions').doc();
        const newLovOptionsDto = {};
        if (_get(data, 'newCity')) {
            _set(newLovOptionsDto, 'newCityOption', _get(data, 'newCity'));
            _set(newLovOptionsDto, 'newCityOptionState', _get(data, 'state', 'Unknown State'));
            _set(newLovOptionsDto, 'newCityOptionCountry', _get(data, 'country', 'Unknown Country'));
        }
        if (!_isEmpty(newLovOptionsDto)) {
            batch.set(newLovOptionRef, {
                ...newLovOptionsDto,
                createDt: currentDt,
                createBy: employerId,
            });
        }

        await batch.commit();

        setSnackbarMessage(snackBarMessage ? snackBarMessage : SNACK_BAR_MESSAGE_SAVE);

        if (callback) callback();
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const uploadProfilePicture = async (file, employerId, /*setProfilePicture,*/ data, setSnackbarMessage, setSnackbarErrorMessage, setProgress, closeDialog) => {
    const db = getFirestore();
    const now = new Date();

    const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`employers/${employerId}/profilePicture/${timestampStr}`);
    const uploadTask = picRef.put(file, {
        contentType: file.type,
        customMetadata: { employerId, origFilename: timestampStr, isEmpProfilePic: true },
    });
    const uploadedImagesRef = db.collection('uploadedImages').doc();

    uploadTask.on(
        'state_changed',
        snapshot => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(progress);
        },
        error => console.error('File upload failed', error),
        async () => {
            try {
                const url = await picRef.getDownloadURL();
                //------------------------------------------------------------------------------------
                // creating Uploaded Image Doc
                //------------------------------------------------------------------------------------
                await uploadedImagesRef.set({
                    createDt: now,
                    isEmpProfilePic: true,
                    status: UPLOADED_VIDEO_STATUS_PROCESSING, // P, F, A, C, D
                    updateDt: now,
                    url,
                    employerId,
                });
                //------------------------------------------------------------------------------------
                // Upadting Employer Data
                //------------------------------------------------------------------------------------
                await updateEmployerData({
                    data: { ...data, updateDt: now, uploadedPictureStatus: UPLOADED_VIDEO_STATUS_PROCESSING },
                    employerId,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                });
                closeDialog();
                setProgress(null);
                // setProfilePicture(url);
            } catch (error) {
                console.log(error);
            }
        },
    );
};

export const deleteProfilePicture = async (employerId, data, setSnackbarMessage, setSnackbarErrorMessage, prevPicUrl) => {
    const timeStamp = prevPicUrl.split('%2F')[3].split('?alt')[0];
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`employers/${employerId}/profilePicture/${timeStamp}`);

    try {
        await picRef.delete();
        updateEmployerData({ data: { ...data, profilePicUrl: '' }, employerId, setSnackbarMessage, setSnackbarErrorMessage });
    } catch (error) {
        console.log(error);
    }
};

export const resetEmpSeResults = ({ employerId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    const employerRef = db.collection('employers').doc(employerId);

    return db
        .runTransaction(transaction => {
            // This code may get re-run multiple times if there are conflicts.
            return transaction.get(employerRef).then(function (doc) {
                if (!doc.exists) {
                    throw new Error('Profile document does not exist!');
                }

                const dto = {
                    seResult: null, // for removing stats old of bad video.
                    uploadedPictureStatus: UPLOADED_VIDEO_STATUS_APPROVED, // for reseting the video status
                };

                transaction.update(employerRef, {
                    ...deleteEmptyFields(dto),
                    updateDt: new Date(),
                });
            });
        })
        .then(function () {
            // console.log('Transaction successfully committed!');
            // dataDidSave();
            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        })
        .catch(function (error) {
            console.error('Save profile type transaction failed: ', error);
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        });
};

export const checkUrlName = async ({ urlName }) => {
    const db = getFirestore();
    const query = db.collection('employers').where('urlNameLower', '==', _toLower(urlName)).limit(1);
    try {
        const querySnapshot = await query.get();
        const count = querySnapshot.size;
        return count > 0;
    } catch (error) {
        console.log('error', error);
        throw error;
    }
};

export const saveEmployerSetupIntentCardPaymentMethod = async ({ employerId, stripeCusId, setupIntent, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // Set card as default payment for future purchases
    //-------------------------------------------------------------------------------------------
    let stripePmId = null;
    if (setupIntent) {
        stripePmId = setupIntent.payment_method;
        try {
            const setDefaultCardSuccess = await setDefaultCardForStripeCustomer({ stripeCusId, stripePmId });
            if (!setDefaultCardSuccess) {
                console.error('Setting default card unsuccessful.');
                throw new Error('Setting default card unsuccessful.');
            }

            //-------------------------------------------------------------------------------------------
            // prep for employers
            //-------------------------------------------------------------------------------------------
            const employerRef = db.collection('employers').doc(employerId);
            try {
                // This code may get re-run multiple times if there are conflicts.
                await db.runTransaction(async transaction => {
                    //-------------------------------------------------------------------------------------------
                    // update employers
                    //-------------------------------------------------------------------------------------------
                    let employerDto = mapDefinedFields({
                        stripeDefaultPmId: stripePmId || undefined,
                    });
                    if (!_isEmpty(employerDto)) await transaction.update(employerRef, employerDto);
                });

                setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
                return true;
                ////
            } catch (error) {
                console.error('Failed to update employer', error);
                throw new Error(`Failed to update employer - ${error.message}`);
            }
        } catch (error) {
            console.error('Failed to set default card.', error);
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
            return false;
        }
    }
};

export const activateSubscription = async ({
    tier,
    userId,
    employerId,
    pendingAccountSetup,
    isSelectedPlanMonthly,
    promoCode,
    // promoCodeMessage,
    promoCodeType,
    // validPromoCodeData,
    stripeCusId,
    createSubscriptionResp = {},
    paymentIntent,
    setupIntent,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();

    const currentDt = new Date();

    // obtain data from the Stripe object
    const { data: createSubscriptionRespData = {} } = createSubscriptionResp;
    const { subscriptionId: stripeSubId, createMs, startMs, endMs } = createSubscriptionRespData;
    const subscriptionDt = createMs ? new Date(createMs * 1000) : null;
    const billCycleStartDt = startMs ? new Date(startMs * 1000) : null;
    const billCycleEndDt = endMs ? new Date(endMs * 1000) : null;

    //TODO fix promoCode
    // const isPromoCodeApplied = Boolean(promoCodeMessage === 'Applied') && promoCode !== '';

    // const { type } = validPromoCodeData;
    // let monthToAdd = 1;

    // if (type === '60D') monthToAdd = 2;

    const billMonthly = isSelectedPlanMonthly ? true : '';

    //-------------------------------------------------------------------------------------------
    // Set card as default payment for future purchases
    //-------------------------------------------------------------------------------------------
    let stripePmId = paymentIntent ? paymentIntent.payment_method : setupIntent ? setupIntent.payment_method : null;
    if (stripePmId) {
        try {
            const setDefaultCardSuccess = await setDefaultCardForStripeCustomer({ stripeCusId, stripePmId });
            if (!setDefaultCardSuccess) console.error('Setting default card unsuccessful.');
        } catch (error) {
            console.error('Failed to set default card.', error);
        }
    }

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for users
    //-------------------------------------------------------------------------------------------
    const userRef = db.collection('users').doc(userId);

    try {
        // This code may get re-run multiple times if there are conflicts.
        await db.runTransaction(async transaction => {
            // -------------------------------------------------------------------------------------------
            // read users
            // -------------------------------------------------------------------------------------------
            let userEmployersDto = null;
            if (pendingAccountSetup) {
                const userDoc = await transaction.get(userRef);

                // get the members array and update the status of the selected employer
                const userEmployers = _get(userDoc.data(), 'employers', []);
                const empIdx = _findIndex(userEmployers, userEmployer => userEmployer.employerId === employerId);
                userEmployersDto = _set(userEmployers, `[${empIdx}].status`, ACCOUNT_STATUS_ACTIVE);
            }

            //-------------------------------------------------------------------------------------------
            // update employers
            //-------------------------------------------------------------------------------------------
            let employerDto = mapDefinedFields({
                tier,
                stripeSubId,
                stripeDefaultPmId: stripePmId || undefined,
                subscriptionDt,
                billMonthly: !!billMonthly,
                billCycleStartDt,
                billCycleEndDt,
                tierNew: null,
                billMonthlyNew: null,
                checkStripe: false,
                pastDue: false,
            });
            if (promoCodeType && promoCodeType === '10%') employerDto = { ...employerDto, promoCodeMonthLeft: 11 };
            if (promoCodeType) employerDto = { ...employerDto, promoCodeApplied: promoCodeType };
            await transaction.update(employerRef, employerDto);

            //-------------------------------------------------------------------------------------------
            // update users
            //-------------------------------------------------------------------------------------------
            if (pendingAccountSetup) {
                await transaction.update(userRef, { employers: userEmployersDto });
            }
            //-------------------------------------------------------------------------------------------
            // updating Promo Code
            //-------------------------------------------------------------------------------------------
            if (!_isNil(promoCode) && promoCode !== '') {
                const promoCodeRef = db.collection('promoCodes').doc(promoCode);
                await transaction.update(promoCodeRef, {
                    employerId,
                    userId,
                    useDt: currentDt,
                });
            }

            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
        });
    } catch (error) {
        console.log('Failed to subscribe', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const activateTier1Subscription = async ({
    userId,
    employerId,
    pendingAccountSetup,
    isSelectedPlanMonthly,
    promoCode,
    // promoCodeMessage,
    promoCodeType,
    // validPromoCodeData,
    // stripeCusId,
    createSubscriptionResp = {},
    // paymentIntent,
    // setupIntent,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();

    const currentDt = new Date();

    // obtain data from the Stripe object
    const { data: createSubscriptionRespData = {} } = createSubscriptionResp;
    const { subscriptionId: stripeSubId, createMs, startMs, endMs } = createSubscriptionRespData;
    const subscriptionDt = createMs ? new Date(createMs * 1000) : null;
    const billCycleStartDt = startMs ? new Date(startMs * 1000) : null;
    const billCycleEndDt = endMs ? new Date(endMs * 1000) : null;

    //TODO fix promoCode
    // const isPromoCodeApplied = Boolean(promoCodeMessage === 'Applied') && promoCode !== '';

    // const { type } = validPromoCodeData;
    // let monthToAdd = 1;

    // if (type === '60D') monthToAdd = 2;

    const billMonthly = isSelectedPlanMonthly ? true : '';

    // //-------------------------------------------------------------------------------------------
    // // Set card as default payment for future purchases
    // //-------------------------------------------------------------------------------------------
    // let stripePmId = paymentIntent ? paymentIntent.payment_method : setupIntent ? setupIntent.payment_method : null;
    // if (stripePmId) {
    //     try {
    //         const setDefaultCardSuccess = await setDefaultCardForStripeCustomer({ stripeCusId, stripePmId });
    //         if (!setDefaultCardSuccess) console.error('Setting default card unsuccessful.');
    //     } catch (error) {
    //         console.error('Failed to set default card.', error);
    //     }
    // }

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for users
    //-------------------------------------------------------------------------------------------
    const userRef = db.collection('users').doc(userId);

    try {
        // This code may get re-run multiple times if there are conflicts.
        await db.runTransaction(async transaction => {
            // -------------------------------------------------------------------------------------------
            // read users
            // -------------------------------------------------------------------------------------------
            let userEmployersDto = null;
            if (pendingAccountSetup) {
                const userDoc = await transaction.get(userRef);

                // get the members array and update the status of the selected employer
                const userEmployers = _get(userDoc.data(), 'employers', []);
                const empIdx = _findIndex(userEmployers, userEmployer => userEmployer.employerId === employerId);
                userEmployersDto = _set(userEmployers, `[${empIdx}].status`, ACCOUNT_STATUS_ACTIVE);
            }

            //-------------------------------------------------------------------------------------------
            // update employers
            //-------------------------------------------------------------------------------------------
            let employerDto = mapDefinedFields({
                tier: 1,
                stripeSubId,
                // stripeDefaultPmId: stripePmId || undefined,
                subscriptionDt,
                billMonthly: !!billMonthly,
                billCycleStartDt,
                billCycleEndDt,
                tierNew: null,
                billMonthlyNew: null,
                checkStripe: false,
                pastDue: false,
            });
            if (promoCodeType && promoCodeType === '10%') employerDto = { ...employerDto, promoCodeMonthLeft: 11 };
            if (promoCodeType) employerDto = { ...employerDto, promoCodeApplied: promoCodeType };
            await transaction.update(employerRef, employerDto);

            //-------------------------------------------------------------------------------------------
            // update users
            //-------------------------------------------------------------------------------------------
            if (pendingAccountSetup) {
                await transaction.update(userRef, { employers: userEmployersDto });
            }

            //-------------------------------------------------------------------------------------------
            // updating Promo Code
            //-------------------------------------------------------------------------------------------
            if (!_isNil(promoCode) && promoCode !== '') {
                const promoCodeRef = db.collection('promoCodes').doc(promoCode);
                await transaction.update(promoCodeRef, {
                    employerId,
                    userId,
                    useDt: currentDt,
                });
            }

            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
        });
    } catch (error) {
        console.log('Failed to subscribe', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

// DEPRECATED
export const subscribe = async ({
    tier,
    userId,
    employerId,
    isSelectedPlanMonthly,
    pendingAccountSetup,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    PromoCode,
    promoCodeMessage,
    promoCodeType,
    validPromoCodeData,
}) => {
    const db = getFirestore();

    const currentDt = new Date();
    const subscriptionDt = currentDt;

    const isPromoCodeApplied = Boolean(promoCodeMessage === 'Applied') && PromoCode !== '';

    const { type } = validPromoCodeData;
    let monthToAdd = 1;

    if (type === '60D') monthToAdd = 2;

    const billMonthly = isSelectedPlanMonthly ? true : '';
    const billCycleStartDt = isPromoCodeApplied ? addMonths(currentDt, monthToAdd) : currentDt;
    const billCycleEndDt = isSelectedPlanMonthly
        ? addMonths(currentDt, isPromoCodeApplied ? monthToAdd + 1 : 1)
        : addYears(isPromoCodeApplied ? addMonths(currentDt, monthToAdd) : currentDt, 1);

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    //-------------------------------------------------------------------------------------------
    // prep for users
    //-------------------------------------------------------------------------------------------
    const userRef = db.collection('users').doc(userId);

    try {
        // This code may get re-run multiple times if there are conflicts.
        await db.runTransaction(async transaction => {
            // -------------------------------------------------------------------------------------------
            // read users
            // -------------------------------------------------------------------------------------------
            let userEmployersDto = null;
            if (pendingAccountSetup) {
                const userDoc = await transaction.get(userRef);
                // get the members array and update the status of the selected employer
                const userEmployers = _get(userDoc.data(), 'employers', []);
                const empIdx = _findIndex(userEmployers, userEmployer => userEmployer.employerId === employerId);
                userEmployersDto = _set(userEmployers, `[${empIdx}].status`, ACCOUNT_STATUS_ACTIVE);
            }

            //-------------------------------------------------------------------------------------------
            // update employers
            //-------------------------------------------------------------------------------------------
            let employerDto = mapDefinedFields({ tier, subscriptionDt, billMonthly, billCycleStartDt, billCycleEndDt });
            if (promoCodeType && promoCodeType === '10%') employerDto = { ...employerDto, promoCodeMonthLeft: 11 };
            if (promoCodeType) employerDto = { ...employerDto, promoCodeApplied: promoCodeType };
            await transaction.update(employerRef, { ...employerDto });

            //-------------------------------------------------------------------------------------------
            // update users
            //-------------------------------------------------------------------------------------------
            if (pendingAccountSetup) {
                await transaction.update(userRef, { employers: userEmployersDto });
            }
            //-------------------------------------------------------------------------------------------
            // updating Promo Code
            //-------------------------------------------------------------------------------------------
            if (!_isNil(PromoCode) && PromoCode !== '') {
                const promoCodeRef = db.collection('promoCodes').doc(PromoCode);
                // if Promo coed is type 10% discount
                // if (promoCodeType === '10%') {
                //     await transaction.update(promoCodeRef, {
                //         lastUseDt: currentDt,
                //         usedBy: getArrayUnion({ employerId, userId, useDt: currentDt }),
                //     });
                // } else {
                await transaction.update(promoCodeRef, {
                    employerId,
                    userId,
                    useDt: currentDt,
                });
                // }
            }

            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        });
    } catch (error) {
        console.log('Failed to subscribe', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const upgradeTierSubscription = async ({
    tier,
    employerId,
    isSelectedPlanMonthly,
    stripeCusId,
    subscriptionResp = {},
    paymentIntent,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();

    // obtain data from the Stripe object
    const { data: subscriptionRespData = {} } = subscriptionResp;
    const { createMs, startMs, endMs } = subscriptionRespData;
    const subscriptionDt = createMs ? new Date(createMs * 1000) : null;
    const billCycleStartDt = startMs ? new Date(startMs * 1000) : null;
    const billCycleEndDt = endMs ? new Date(endMs * 1000) : null;

    const billMonthly = isSelectedPlanMonthly ? true : '';

    //-------------------------------------------------------------------------------------------
    // Set card as default payment for future purchases
    //-------------------------------------------------------------------------------------------
    let stripePmId = null;
    if (paymentIntent) {
        stripePmId = paymentIntent.payment_method;
        try {
            const setDefaultCardSuccess = await setDefaultCardForStripeCustomer({ stripeCusId, stripePmId });
            if (!setDefaultCardSuccess) console.error('Setting default card unsuccessful.');
        } catch (error) {
            console.error('Failed to set default card.', error);
        }
    }

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    try {
        // This code may get re-run multiple times if there are conflicts.
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // update employers
            //-------------------------------------------------------------------------------------------
            let employerDto = mapDefinedFields({
                tier,
                stripeDefaultPmId: stripePmId || undefined,
                subscriptionDt,
                billMonthly: !!billMonthly,
                billCycleStartDt,
                billCycleEndDt,
                tierNew: null,
                billMonthlyNew: null,
                checkStripe: false,
                pastDue: false,
                jsEngageCount: 0,
            });
            await transaction.update(employerRef, employerDto);

            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
        });
    } catch (error) {
        console.log('Failed to update subscription', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

// DEPRECATED
export const upgradeSubscription = async ({ tier, employerId, isSelectedPlanMonthly, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const batch = db.batch();

    const currentDt = new Date();

    const billCycleStartDt = currentDt;
    const billCycleEndDt = isSelectedPlanMonthly ? addMonths(currentDt, 1) : addYears(currentDt, 1);

    const employerRef = db.collection('employers').doc(employerId);
    try {
        let _data = deleteEmptyFields({
            tier,
            billMonthly: !!isSelectedPlanMonthly,
            billCycleStartDt,
            billCycleEndDt,
            downgradeSubscriptionDt: '',
            unsubscribeDt: '',
            tierNew: '',
            membersNew: '',
        });
        batch.update(employerRef, { ..._data });
        await batch.commit();
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const cancelAdditionalSeatSubscription = async ({ isRefund, stripeSubId, stripeAdditionalSeatSubId, employerId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const batch = db.batch();

    const now = new Date();

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);
    // const employerDto = { inactive: true, unsubscribeDt: now };

    try {
        if (isRefund) {
            //-------------------------------------------------------------------------------------------
            // commit batch updates
            //-------------------------------------------------------------------------------------------
            await batch.commit();

            //-------------------------------------------------------------------------------------------
            // cancel Stripe subscription with full refund
            //-------------------------------------------------------------------------------------------
            const resp = await requestCancelNewAdditionalSeatSubscriptionNow({ stripeSubId, stripeAdditionalSeatSubId });
            if (resp?.error) {
                console.log('Failed to cancel Additional Seat subscription', resp?.error);
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return false;
            }

            // NOTE: Stripe webhook Cloud Functions will update our records in Firestore

            // workflow stops here
            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
            ////
        } else {
            // it passed 48 hours and we will schedule cancellation with no refund
            //-------------------------------------------------------------------------------------------
            // update Stripe subscription
            //-------------------------------------------------------------------------------------------
            const resp = await requestCancelAdditionalSeatSubscriptionAtPeriodEnd({ stripeAdditionalSeatSubId });
            if (resp?.error) {
                console.log('Failed to update Additional Seat subscription to cancel at period end', resp?.error);
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return false;
            }

            //-------------------------------------------------------------------------------------------
            // write employers
            //-------------------------------------------------------------------------------------------
            batch.update(employerRef, { additionalSeatUnsubscribeDt: now });

            //-------------------------------------------------------------------------------------------
            // commit batch updates
            //-------------------------------------------------------------------------------------------
            await batch.commit();

            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
        }
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const cancelSubscription = async ({
    isRefund,
    stripeSubId,
    stripeAdditionalSeatSubId,
    name,
    email,
    employerId,
    tier,
    profilePicUrl,
    feedback,
    reason,
    userId,
    companyName,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();
    const batch = db.batch();

    const now = new Date();

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);
    // const employerDto = { inactive: true, unsubscribeDt: now };

    //-------------------------------------------------------------------------------------------
    // prep for feedback
    //-------------------------------------------------------------------------------------------
    const feedbackRef = db.collection('feedback').doc();
    let feedbackDto = mapDefinedFields({ name, email, employerId, tier, profilePicUrl, feedback, reason, userId, companyName, createDt: now });

    try {
        if (isRefund) {
            //-------------------------------------------------------------------------------------------
            // write feedback
            //-------------------------------------------------------------------------------------------
            batch.set(feedbackRef, feedbackDto);

            //-------------------------------------------------------------------------------------------
            // commit batch updates
            //-------------------------------------------------------------------------------------------
            await batch.commit();

            //-------------------------------------------------------------------------------------------
            // cancel Stripe subscription with full refund
            //-------------------------------------------------------------------------------------------
            const resp = await requestCancelNewSubscriptionNow({ stripeSubId, stripeAdditionalSeatSubId });
            if (resp?.error) {
                console.log('Failed to cancel Tier subscription', resp?.error);
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return false;
            }

            // NOTE: Stripe webhook Cloud Functions will update our records in Firestore

            // workflow stops here
            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
            ////
        } else {
            // it passed 48 hours and we will schedule cancellation with no refund
            //-------------------------------------------------------------------------------------------
            // update Stripe subscription
            //-------------------------------------------------------------------------------------------
            const resp = await requestCancelSubscriptionAtPeriodEnd({ stripeSubId, stripeAdditionalSeatSubId });
            if (resp?.error) {
                console.log('Failed to update Tier subscription to cancel at period end', resp?.error);
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return false;
            }

            //-------------------------------------------------------------------------------------------
            // write feedback
            //-------------------------------------------------------------------------------------------
            batch.set(feedbackRef, feedbackDto);

            //-------------------------------------------------------------------------------------------
            // write employers
            //-------------------------------------------------------------------------------------------
            batch.update(employerRef, { unsubscribeDt: now });

            //-------------------------------------------------------------------------------------------
            // commit batch updates
            //-------------------------------------------------------------------------------------------
            await batch.commit();

            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            return true;
        }
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

// DEPRECATED
export const unsubscribe = async ({ data, employerId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const batch = db.batch();

    const currentDt = new Date();

    const employerRef = db.collection('employers').doc(employerId);
    const feedbackRef = db.collection('feedback').doc();

    try {
        let _data = mapDefinedFields({ ...data, createDt: currentDt });

        batch.update(employerRef, { unsubscribeDt: currentDt });
        batch.set(feedbackRef, { ..._data });

        await batch.commit();
        // await getFirebaseAuth().signOut();

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const sendInvitation = async ({
    email,
    existingInvited,
    employerId,
    existingMembers,
    inviterUserId,
    isAdmin = false,
    callback,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();
    const batch = db.batch();

    const lowerCaseEmail = _toLower(email);
    // Check if the email address belongs to an existing member
    const emailExistsInMembers = _some(existingMembers, member => member.email === lowerCaseEmail);
    if (emailExistsInMembers) {
        setSnackbarErrorMessage(ERROR_MESSAGE_INVITEE_EXISTS_IN_MEMBERS);
        return;
    } else {
        // Check if the email address is pending invitation
        const emailInvitationPending = _some(existingInvited, invitedEmail => invitedEmail === lowerCaseEmail);
        if (emailInvitationPending) {
            setSnackbarErrorMessage(ERROR_MESSAGE_INVITEE_PENDING);
            return;
        }
    }

    // Inviting new member
    const currentDt = new Date();

    const employerRef = db.collection('employers').doc(employerId);
    const invitationRef = db.collection('invitations').doc();

    try {
        let _data = mapDefinedFields({ email: lowerCaseEmail, inviteDt: currentDt, employerId, acceptDt: null, declineDt: null, inviterUserId });

        batch.update(employerRef, { invited: [...existingInvited, lowerCaseEmail] });
        batch.set(invitationRef, { ..._data, isAdmin });

        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_INVITATION);

        if (callback) callback({ code: invitationRef.id });
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

// export const removeInvitee = async (email, invited, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
//     const db = getFirestore();
//     const batch = db.batch();

//     const employerRef = db.collection('employers').doc(employerId);
//     const invitationRef = db
//         .collection('invitations')
//         .where('email', '==', _toLower(email))
//         .where('employerId', '==', employerId)
//         .where('acceptDt', '==', null)
//         .where('declineDt', '==', null)
//         .limit(1);
//     try {
//         batch.update(employerRef, { invited, inviteDeclined: getArrayRemove(email) });
//         const querySnapshot = await invitationRef.get();
//         querySnapshot.forEach(doc => batch.delete(doc.ref));

//         await batch.commit();

//         setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
//     } catch (error) {
//         console.log('error', error);
//         setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
//     }
// };

export const removeInvitee = async (email, invited, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();

    try {
        //-------------------------------------------------------------------------------------------
        // prep for employers
        //-------------------------------------------------------------------------------------------
        const employerRef = db.collection('employers').doc(employerId);

        //-------------------------------------------------------------------------------------------
        // prep for invitations
        //-------------------------------------------------------------------------------------------
        const invitationsQuery = db
            .collection('invitations')
            .where('email', '==', _toLower(email))
            .where('employerId', '==', employerId)
            .where('acceptDt', '==', null)
            .where('declineDt', '==', null)
            .limit(1);
        let invitationRef = null;

        //-------------------------------------------------------------------------------------------
        // prep for openInvitations
        //-------------------------------------------------------------------------------------------
        const openInvitationRef = db.collection('openInvitations').doc(_toLower(email));

        //-------------------------------------------------------------------------------------------
        // read invitations
        //-------------------------------------------------------------------------------------------
        const invitationsQuerySnapshot = await invitationsQuery.get();
        invitationsQuerySnapshot.forEach(doc => {
            invitationRef = doc.ref;
        });

        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // write employers
            //-------------------------------------------------------------------------------------------
            transaction.update(employerRef, { invited, inviteDeclined: getArrayRemove(email) });

            // assume invitee has not accepted invitation but double check before deleting invitation and decrementing open invitation count
            if (invitationRef) {
                //-------------------------------------------------------------------------------------------
                // write invitations
                //-------------------------------------------------------------------------------------------
                await transaction.delete(invitationRef);

                //-------------------------------------------------------------------------------------------
                // write openInvitations
                //-------------------------------------------------------------------------------------------
                await transaction.update(openInvitationRef, { count: getFirestoreIncrementField(-1) });
            }
        });

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('Failed to remove member invitation: ', { error });
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const removeMember = async (members, membersNew, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const batch = db.batch();

    const employerRef = db.collection('employers').doc(employerId);
    try {
        batch.update(employerRef, { members, membersNew });
        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_DELETE_TEAM_MEMBER);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const resendInvitation = async ({ email, employerId, callback, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const batch = db.batch();

    let invitationDocId = null;
    const currentDt = new Date();
    const invitationRef = db.collection('invitations').where('email', '==', _toLower(email)).where('employerId', '==', employerId).limit(1);

    try {
        const querySnapshot = await invitationRef.get();
        querySnapshot.forEach(doc => {
            batch.update(doc.ref, { inviteDt: currentDt });
            invitationDocId = doc.id;
        });

        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_INVITATION);

        if (callback) callback({ code: invitationDocId });
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const changeInviteeEmail = async (email, prevEmail, invited, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const batch = db.batch();

    const currentDt = new Date();

    const employerRef = db.collection('employers').doc(employerId);
    const invitationRef = db.collection('invitations').where('email', '==', _toLower(prevEmail));

    try {
        const querySnapshot = await invitationRef.get();
        querySnapshot.forEach(doc => batch.update(doc.ref, { email: _toLower(email), inviteDt: currentDt }));

        batch.update(employerRef, { invited });

        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const toggleMembersRole = async (members, membersNew = null, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const batch = db.batch();

    const employerRef = db.collection('employers').doc(employerId);
    try {
        batch.update(employerRef, { members, membersNew });
        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};
export const makeMemberOwner = async ({ updatedMembers, employerId, setSnackbarMessage, setSnackbarErrorMessage, memberName }) => {
    const db = getFirestore();
    const batch = db.batch();

    const employerRef = db.collection('employers').doc(employerId);
    try {
        batch.update(employerRef, { members: updatedMembers });
        await batch.commit();

        setSnackbarMessage(`Updated ${memberName} as new owner successfully!`);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const saveEmpMember = async ({
    employerId,
    userId,
    firstName,
    middleName,
    lastName,
    country,
    state,
    city,
    cityLocation,
    newCity,
    profilePicUrl,
    uploadedPictureStatus,
    isSkipActivateAccount,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);
    const memberDto = { firstName, middleName, lastName, country, state, city, cityLocation, profilePicUrl, uploadedPictureStatus };

    try {
        // This code may get re-run multiple times if there are conflicts.
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);

            if (employerDoc.exists) {
                const data = employerDoc.data();
                const members = data?.members || [];
                const idx = _findIndex(members, item => item.userId === userId);

                if (idx >= 0) {
                    // update data in members
                    const member = members[idx];
                    _set(members, `[${idx}]`, { ...member, ...mapDefinedFields(memberDto) });

                    // update data in membersNew
                    const membersNew = data?.membersNew || [];
                    const memberNewIdx = _findIndex(membersNew, item => item.userId === userId);
                    const memberNew = membersNew[memberNewIdx];
                    _set(membersNew, `[${memberNewIdx}]`, { ...memberNew, ...mapDefinedFields(memberDto) });

                    //-------------------------------------------------------------------------------------------
                    // write employers
                    //-------------------------------------------------------------------------------------------
                    await transaction.set(employerRef, { members, membersNew }, { merge: true });
                    setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
                } else {
                    throw new Error('Employer member record cannot be found.');
                }
            } else {
                throw new Error('Employer record cannot be found.');
            }

            //------------------------------------------------------------------------------------
            // Add new LOV options (if any)
            //------------------------------------------------------------------------------------
            const batch = db.batch();
            const newLovOptionRef = db.collection('newLovOptions').doc();
            const newLovOptionsDto = {};

            if (newCity) {
                _set(newLovOptionsDto, 'newCityOption', newCity);
                _set(newLovOptionsDto, 'newCityOptionState', state || 'Unknown State');
                _set(newLovOptionsDto, 'newCityOptionCountry', country || 'Unknown Country');
            }

            if (!_isEmpty(newLovOptionsDto)) {
                batch.set(newLovOptionRef, {
                    ...newLovOptionsDto,
                    createDt: new Date(),
                    createBy: userId,
                });
            }

            //------------------------------------------------------------------------------------
            // commit new LOV options batch changes
            //------------------------------------------------------------------------------------
            await batch.commit();
        });

        //------------------------------------------------------------------------------------
        // If isActivateAccount is true, attempt to set the account status in user doc to active.
        //------------------------------------------------------------------------------------
        if (!isSkipActivateAccount) await activeEmpMemberAfterSetUp({ userId, employerId });

        return true;
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

// DEPRECATED
export const updateMemberInfo = async (members, membersNew, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const batch = db.batch();
    const employerRef = db.collection('employers').doc(employerId);

    try {
        batch.update(employerRef, { members, membersNew });
        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const uploadEmpMemberProfilePic = async ({ file, employerId, userId, /*setProfilePicture,*/ setSnackbarMessage, setSnackbarErrorMessage, setProgress, callback }) => {
    const db = getFirestore();
    const now = new Date();

    const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`employers/${employerId}/members/${userId}/profilePicture/${timestampStr}`);
    const uploadTask = picRef.put(file, {
        contentType: file.type,
        customMetadata: { employerId, userId, origFilename: timestampStr, isEmpMemberProfilePic: 'Y' },
    });

    const uploadedImagesRef = db.collection('uploadedImages').doc();

    uploadTask.on(
        'state_changed',
        snapshot => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress && setProgress(progress);
        },
        error => console.error('File upload failed', error),
        async () => {
            try {
                const url = await picRef.getDownloadURL();
                //------------------------------------------------------------------------------------
                // creating Uploaded Image Doc
                //------------------------------------------------------------------------------------
                await uploadedImagesRef.set({
                    createDt: now,
                    isEmpMemberProfilePic: true,
                    status: UPLOADED_VIDEO_STATUS_PROCESSING, // P, F, A, C, D
                    updateDt: now,
                    url,
                    employerId,
                    userId,
                });

                await saveEmpMember({
                    employerId,
                    userId,
                    // profilePicUrl: url,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                    uploadedPictureStatus: UPLOADED_VIDEO_STATUS_PROCESSING,
                });

                // setProfilePicture && setProfilePicture(url);
                setProgress && setProgress(null);
                callback && callback();
            } catch (error) {
                console.log(error);
            }
        },
    );
};

export const resetEmpMemberPictureSeResults = async ({ employerId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    const employerRef = db.collection('employers').doc(employerId);
    try {
        await db.runTransaction(async transaction => {
            //-------------------------------------------------------------------------------------------
            // read employers
            //-------------------------------------------------------------------------------------------
            const employerDoc = await transaction.get(employerRef);

            if (employerDoc.exists) {
                const data = employerDoc.data();
                let members = data?.members || [];
                members = members?.map(member => {
                    const dto = {
                        ...member,
                        // remove Sight Engine Results and reset picture state
                        seResult: null, // for removing stats old of bad video.
                        uploadedPictureStatus: UPLOADED_VIDEO_STATUS_APPROVED, // for reseting the video status
                    };
                    return dto;
                });

                //-------------------------------------------------------------------------------------------
                // write employers
                //-------------------------------------------------------------------------------------------
                await transaction.set(employerRef, { members }, { merge: true });
                setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            } else {
                throw new Error('Employer record cannot be found.');
            }
        });
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

// DEPRECATED
export const uploadMemberProfilePicture = async ({
    file,
    employerId,
    userId,
    setProfilePicture,
    members,
    membersNew,
    selectedUser,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    setProgress,
    closeDialog,
}) => {
    const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`employers/${employerId}/members/${userId}/profilePicture/${timestampStr}`);
    const uploadTask = picRef.put(file, { contentType: file.type });

    uploadTask.on(
        'state_changed',
        snapshot => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(progress);
        },
        error => console.error('File upload failed', error),
        async () => {
            try {
                const url = await picRef.getDownloadURL();
                closeDialog();
                setProgress(null);
                setProfilePicture(url);
                updateMemberInfo(
                    [...members.filter(el => el.userId !== selectedUser.userId), { ...selectedUser, profilePicUrl: url }],
                    membersNew.find(el => el.userId === selectedUser.userId)
                        ? [...membersNew.filter(el => el.userId !== selectedUser.userId), { ...selectedUser, profilePicUrl: url }]
                        : membersNew,

                    employerId,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                );
            } catch (error) {
                console.log(error);
            }
        },
    );
};

export const deleteEmpMemberProfilePic = async ({ prevPicUrl, employerId, userId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const timeStamp = prevPicUrl.split('%2F')[3].split('?alt')[0];
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`employers/${employerId}/members/${userId}/profilePicture/${timeStamp}`);

    try {
        await picRef.delete();

        await saveEmpMember({ employerId, userId, profilePicUrl: null, setSnackbarMessage, setSnackbarErrorMessage });
        return true;
    } catch (error) {
        console.log(error);
        return false;
    }
};

// DEPRECATED
export const deleteMemberProfilePicture = async ({ members, membersNew, selectedUser, prevPicUrl, employerId, userId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const timeStamp = prevPicUrl.split('%2F')[3].split('?alt')[0];
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`employers/${employerId}/members/${userId}/profilePicture/${timeStamp}`);

    try {
        await picRef.delete();
        updateMemberInfo(
            [...members.filter(el => el.userId !== selectedUser.userId), { ...selectedUser, profilePicUrl: '' }],
            membersNew.find(el => el.userId === selectedUser.userId)
                ? [...membersNew.filter(el => el.userId !== selectedUser.userId), { ...selectedUser, profilePicUrl: '' }]
                : membersNew,
            employerId,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        );
    } catch (error) {
        console.log(error);
    }
};

export const changeMemberEmail = async (email, members, membersNew, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const batch = db.batch();

    const user = getFirebaseAuth().currentUser;

    const employerRef = db.collection('employers').doc(employerId);

    try {
        await user.updateEmail(_toLower(email));

        batch.update(employerRef, { members, membersNew });
        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const changeMemberPassword = async (password, setSnackbarMessage, setSnackbarErrorMessage) => {
    const user = getFirebaseAuth().currentUser;

    try {
        await user.updatePassword(password);

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const downgradeAdditionalSeatSubscription = async ({
    additionalSeatsNew,
    employerId,
    stripeAdditionalSeatSubId,
    existingAdditionalSeats,
    callback,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();
    // const batch = db.batch();

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    try {
        //-------------------------------------------------------------------------------------------
        // downgrade Stripe subscription
        //-------------------------------------------------------------------------------------------
        const scheduleDowngradeResponse = await scheduleStripeSubscriptionDowngrade({
            isAdditionalSeat: true,
            stripeAdditionalSeatSubId,
            quantity: additionalSeatsNew,
            existingQuantity: existingAdditionalSeats,
        });

        await db.runTransaction(async transaction => {
            if (scheduleDowngradeResponse) {
                let dto = deleteEmptyFields({
                    additionalSeatsNew,
                    downgradeAdditionalSeatDt: new Date(),
                    unsubscribeDt: '',
                });

                //-------------------------------------------------------------------------------------------
                // write employers
                //-------------------------------------------------------------------------------------------
                await transaction.update(employerRef, dto);

                callback && callback();

                setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
                return true;
            } else {
                console.error('Stripe response was null');
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return false;
            }
        });
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const downgradeTierSubscription = async ({
    membersNew,
    tierNew,
    billMonthlyNew,
    employerId,
    stripeSubId,
    stripeAdditionalSeatSubId,
    existingTier,
    existingMonthly,
    callback,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    const db = getFirestore();
    // const batch = db.batch();

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);
    let members = [];

    try {
        //-------------------------------------------------------------------------------------------
        // downgrade Stripe subscription
        //-------------------------------------------------------------------------------------------
        const monthly = _isNil(billMonthlyNew) ? existingMonthly : billMonthlyNew;
        const scheduleDowngradeResponse = await scheduleStripeSubscriptionDowngrade({
            stripeSubId,
            stripeAdditionalSeatSubId,
            tier: tierNew,
            monthly,
            existingTier,
            existingMonthly,
        });

        if (tierNew === 2 || tierNew === 1) {
            // downgrading to Tier 2 or 1, cancel Additional Seat subscription
            await requestCancelSubscriptionAtTierPeriodEnd({ stripeSubId, stripeAdditionalSeatSubId });
        } else if (tierNew === 3 && !_isNil(billMonthlyNew) && billMonthlyNew) {
            // downgrading Tier 3 to Monthly, if Additional Seat subscription is scheduled to be cancelled at period end, undo it.
            await requestCancelSubscriptionAtTierPeriodEnd({ stripeSubId, stripeAdditionalSeatSubId, isUndo: true });
        }

        await db.runTransaction(async transaction => {
            if (scheduleDowngradeResponse) {
                if (!membersNew) {
                    //-------------------------------------------------------------------------------------------
                    // read employers
                    //-------------------------------------------------------------------------------------------
                    const employerDoc = await transaction.get(employerRef);
                    if (employerDoc.exists) {
                        members = _get(employerDoc.data(), 'members');
                    } else {
                        console.error('Employer record not found.');
                        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                    }
                }

                let dto = deleteEmptyFields({
                    membersNew: membersNew || members,
                    tierNew,
                    billMonthlyNew: monthly,
                    downgradeSubscriptionDt: new Date(),
                    unsubscribeDt: '',
                });

                //-------------------------------------------------------------------------------------------
                // write employers
                //-------------------------------------------------------------------------------------------
                await transaction.update(employerRef, dto);

                callback && callback();

                setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
                return true;
            } else {
                console.error('Stripe response was null');
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                return false;
            }
        });
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

// DEPRECATED
export const downgradeSubscription = async (data, employerId, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const batch = db.batch();

    const employerRef = db.collection('employers').doc(employerId);
    try {
        let _data = deleteEmptyFields({ ...data, downgradeSubscriptionDt: new Date(), unsubscribeDt: '' });
        batch.update(employerRef, { ..._data });
        await batch.commit();
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const activateAdditionalSeats = async ({ additionalSeats, employerId, subscriptionResp = {}, isNewSubscription, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const batch = db.batch();

    // obtain data from the Stripe object
    const { data: subscriptionRespData = {} } = subscriptionResp;
    const { subscriptionId: stripeAdditionalSeatSubId, startMs, endMs } = subscriptionRespData;
    const additionalSeatBillCycleStartDt = startMs ? new Date(startMs * 1000) : null;
    const additionalSeatBillCycleEndDt = endMs ? new Date(endMs * 1000) : null;
    const additionalSeatSubscriptionDt = additionalSeatBillCycleStartDt;

    //-------------------------------------------------------------------------------------------
    // prep for employers
    //-------------------------------------------------------------------------------------------
    const employerRef = db.collection('employers').doc(employerId);

    try {
        // prepare 2 DTO.  If subscription already exists, do not include the subscription ID
        const employerDtoExisting = mapDefinedFields({
            additionalSeats,
            additionalSeatSubscriptionDt,
            additionalSeatBillCycleStartDt,
            additionalSeatBillCycleEndDt,
            additionalSeatCheckStripe: false,
            additionalSeatsNew: null,
        });

        const employerDto = mapDefinedFields({
            additionalSeats,
            stripeAdditionalSeatSubId,
            additionalSeatSubscriptionDt,
            additionalSeatBillCycleStartDt,
            additionalSeatBillCycleEndDt,
            additionalSeatCheckStripe: false,
            additionalSeatsNew: null,
        });

        //-------------------------------------------------------------------------------------------
        // write employers
        //-------------------------------------------------------------------------------------------
        batch.update(employerRef, isNewSubscription ? employerDto : employerDtoExisting);

        //-------------------------------------------------------------------------------------------
        // commit
        //-------------------------------------------------------------------------------------------
        await batch.commit();

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);

        return true;
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);

        return false;
    }
};

// DEPRECATED
export const buyAdditionalSeats = async ({ additionalSeats, employerId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const batch = db.batch();

    const employerRef = db.collection('employers').doc(employerId);
    try {
        let _data = mapDefinedFields({ additionalSeats });
        batch.update(employerRef, { ..._data });
        await batch.commit();
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const fetchActiveJobs = (employerId, setJob) => {
    const db = getFirestore();
    const jobsRef = db.collection('jobs').where('employerId', '==', employerId).where('active', '==', true);
    let jobs = [];
    jobsRef.onSnapshot(docSnapshot => {
        jobs = docSnapshot.docs.map(doc => {
            let data = doc.data();
            return convertFirebaseTimestampsToDate({ ...data, uid: doc.id }, ['createDt', 'updateDt']);
        });
        return setJob(jobs);
    });
};

export const validatePromoCode = async ({ promoCode, setValidPromoCodeData }) => {
    try {
        const db = getFirestore();
        const promoCodeRef = db.collection('promoCodes').doc(promoCode);
        const promoCodeDoc = await promoCodeRef.get();

        if (!promoCodeDoc.exists) return { error: true };
        let PromoCode = promoCodeDoc.data();
        if (PromoCode.useDt) return { error: true };
        setValidPromoCodeData(PromoCode);
        return { error: false };
    } catch (error) {
        console.log('error in promo code.', error);
    }
};
